import { Box, Checkbox, Text } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Input, OneModal, Textarea } from '@factorialco/f0-react/dist/experimental'
import {
  FormWithSimpleMaterializedFormsFragment,
  UpdateTrainingFormDocument,
} from 'generated/resources/graphql'

import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

interface EditTrainingForm {
  title: string
  description: string
  anonymous: boolean
}

interface Props {
  onClose: () => void
  onSuccess: () => void
  onError: () => void
  isOpen: boolean
  trainingForm: FormWithSimpleMaterializedFormsFragment
}

const EditTrainingFormModal = ({
  onClose,
  onSuccess,
  onError,
  isOpen = false,
  trainingForm,
}: Props) => {
  const [updateForm, { loading: updatingForm }] = useMutation(UpdateTrainingFormDocument, {
    extractorFn: (data) => data.formsMutations.updateFormsForm,
  })
  const form = useForm<EditTrainingForm>({
    validateOnBlur: false,
    validateOnChange: false,
    defaultValues: {
      title: trainingForm.title ?? '',
      description: trainingForm.description ?? '',
      anonymous: trainingForm.anonymous ?? false,
    },
    validate(values) {
      const errors: FormErrors<EditTrainingForm> = {}

      if (!values.title || values.title.length === 0) {
        errors.title = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (values, { setErrors }) => {
      const result = await updateForm({
        variables: {
          ...values,
          id: trainingForm.id,
        },
      })

      const handler = handleResult(result, (errors) => setErrors(errors))

      if (handler.success) {
        onSuccess()
      } else {
        onError()
      }

      return handler
    },
  })

  return (
    <OneModal isOpen={isOpen} onClose={onClose}>
      <OneModal.Header title={i18n.t('trainings.surveys.create.edit_modal.title')} />
      <OneModal.Content withPadding>
        <form onSubmit={form.handleSubmit} id="updateTrainingForm">
          <Box gap="s24">
            <Box gap="s16">
              <Text size="400" weight="bold">
                {i18n.t('surveys.configuration.basic_info.title')}
              </Text>
              <Box gap="s12">
                <Input
                  label={i18n.t('surveys.configuration.basic_info.name')}
                  value={form.values.title}
                  onChange={(value) => form.setFieldValue('title', value)}
                  error={form.errors.title}
                />
                <Textarea
                  label={i18n.t('surveys.configuration.basic_info.description')}
                  value={form.values.description}
                  onChange={(value) => form.setFieldValue('description', value)}
                  error={form.errors.description}
                />
              </Box>
            </Box>
            {trainingForm.formType === 'satisfaction' && (
              <Box gap="s16">
                <Text size="400" weight="bold">
                  {i18n.t('surveys.configuration.privacy_settings.title')}
                </Text>
                <Box gap="s4">
                  <Checkbox
                    checked={form.values.anonymous}
                    label={i18n.t('surveys.configuration.privacy_settings.anonymous_answers')}
                    onChange={(event) => form.setFieldValue('anonymous', event.target.checked)}
                  />
                  <Text size="200" color="grey700">
                    {i18n.t('surveys.configuration.privacy_settings.anonymous_answers_description')}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        </form>
      </OneModal.Content>
      <OneModal.Footer>
        <Box flexDirection="row" gap="s12" justifyContent="flexEnd" width="full">
          <F0Button onClick={onClose} label={i18n.t('common.cancel')} variant="outline" />
          <F0Button
            type="submit"
            form="updateTrainingForm"
            label={i18n.t('common.save')}
            variant="default"
            loading={form.isSubmitting || updatingForm}
          />
        </Box>
      </OneModal.Footer>
    </OneModal>
  )
}

export default EditTrainingFormModal
